import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kula-ai/components';
import { XClose } from '@kula-ai/icons-core';
import { BAR_BUTTON_HEIGHT_PX } from '../../constants';
import type { OpenTab } from '../../stores';
import type { SessionTabProps } from './SessionTab.types';

// `group` lets the close × fade in on hover (group-hover modifier below).
// max-w + min-w-0 on the title span are what actually let the title truncate
// — without min-w-0 the flex child won't shrink below its content width.
const COMPACT_CLASS =
  `group !h-[${BAR_BUTTON_HEIGHT_PX}px] !min-h-[${BAR_BUTTON_HEIGHT_PX}px] ` +
  `!py-0 !px-2.5 text-xs leading-none max-w-[220px]`;

const STATUS_COLOR_CLASS: Record<OpenTab['status'], string> = {
  idle: 'bg-secondary-300',
  streaming: 'bg-warning-500',
  done: 'bg-success-500',
  error: 'bg-error-500',
};

const ACTIVE_CLASSNAME =
  `bg-secondary-800 text-base-white hover:bg-secondary-800 ` +
  `active:bg-secondary-800 ${COMPACT_CLASS}`;

const INACTIVE_CLASSNAME =
  `bg-base-white text-secondary-800 no-border border-secondary-100 ` +
  `hover:bg-secondary-50 ${COMPACT_CLASS}`;

export const SessionTab = ({
  tab,
  isActive,
  onSelect,
  onClose,
}: SessionTabProps) => {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Re-measure on title change + viewport resize so the tooltip only attaches
  // when the title actually overflows the 160px max-width. JSDOM (test env)
  // doesn't ship ResizeObserver — fall back to a one-shot measure.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;
    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tab.title]);

  const button = (
    <Button
      size="sm"
      aria-label={`Open ${tab.title}`}
      className={isActive ? ACTIVE_CLASSNAME : INACTIVE_CLASSNAME}
      onClick={() => onSelect(tab.sessionId)}
      leftElement={
        <Box
          as="span"
          w="6px"
          h="6px"
          borderRadius="full"
          className={STATUS_COLOR_CLASS[tab.status]}
          aria-hidden
        />
      }
      rightElement={
        <Flex
          as="span"
          role="button"
          aria-label={`Close ${tab.title}`}
          tabIndex={0}
          align="center"
          justify="center"
          w="14px"
          h="14px"
          borderRadius="sm"
          cursor="pointer"
          opacity={0}
          transition="opacity 0.12s"
          className="group-hover:opacity-100 group-focus-within:opacity-100"
          onClick={(event) => {
            event.stopPropagation();
            onClose(tab.sessionId);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onClose(tab.sessionId);
          }}
          _hover={{ bg: 'rgba(0,0,0,0.1)' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'primary-300',
            outlineOffset: '1px',
            opacity: 1,
          }}
        >
          <XClose size={12} color="currentColor" />
        </Flex>
      }
    >
      <Box
        as="span"
        ref={titleRef}
        display="inline-block"
        minW={0}
        maxW="160px"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        verticalAlign="middle"
      >
        {tab.title}
      </Box>
    </Button>
  );

  // Always wrap in Tooltip so hover state is wired on first render. Skip the
  // TooltipContent when the title isn't truncated — radix won't open the
  // popup if there's nothing to render. Wrapping conditionally would mount
  // the trigger AFTER the user starts hovering and the popover never opens.
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      {isTruncated && (
        <TooltipContent style={{ zIndex: 2100 }}>{tab.title}</TooltipContent>
      )}
    </Tooltip>
  );
};
